import { KeyRound } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  useBootstrap,
  useCodeSignIn,
  usePasskeySignIn,
  usePasswordSignIn,
} from "../api/queries.js";
import { LanguageSwitch, Wordmark } from "../ui/brand.js";
import { ErrorMessage, fieldError } from "../ui/feedback.js";
import { Button, Card, TextField } from "../ui/primitives.js";

/** Centered single-card layout of the signed-out screens. */
export function AuthLayout({
  title,
  lead,
  children,
}: {
  title: string;
  lead?: string | undefined;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-6 px-4 py-10">
      <div className="flex items-center justify-between">
        <Wordmark />
        <LanguageSwitch />
      </div>
      <Card className="flex flex-col gap-5">
        <div>
          <h1 className="text-xl font-semibold">{title}</h1>
          {lead && <p className="mt-1 text-sm text-muted">{lead}</p>}
        </div>
        {children}
      </Card>
      <p className="text-center text-xs text-muted">{t("app.tagline")}</p>
    </main>
  );
}

const value = (form: FormData, name: string) => String(form.get(name) ?? "").trim();

export function SetupPage({ codeRequired }: { codeRequired: boolean }) {
  const { t, i18n } = useTranslation();
  const bootstrap = useBootstrap();
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const password = String(form.get("password") ?? "");
    bootstrap.mutate({
      organizationName: value(form, "organizationName"),
      name: value(form, "name"),
      email: value(form, "email"),
      locale: i18n.resolvedLanguage === "ru" ? "ru" : "en",
      ...(password ? { password } : {}),
      ...(codeRequired ? { setupCode: value(form, "setupCode") } : {}),
    });
  };
  return (
    <AuthLayout title={t("setup.title")} lead={t("setup.lead")}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        {codeRequired && (
          <TextField
            label={t("setup.code")}
            name="setupCode"
            required
            autoComplete="off"
            spellCheck={false}
            hint={t("setup.codeHint")}
            error={fieldError(bootstrap.error, "setupCode", t)}
          />
        )}
        <TextField
          label={t("setup.organization")}
          name="organizationName"
          required
          maxLength={100}
        />
        <TextField
          label={t("setup.name")}
          name="name"
          required
          autoComplete="name"
          maxLength={100}
        />
        <TextField
          label={t("setup.email")}
          name="email"
          type="email"
          required
          autoComplete="email"
          error={fieldError(bootstrap.error, "email", t)}
        />
        <TextField
          label={t("setup.password")}
          name="password"
          type="password"
          minLength={12}
          autoComplete="new-password"
          hint={t("setup.passwordHint")}
          error={fieldError(bootstrap.error, "password", t)}
        />
        <ErrorMessage error={bootstrap.error} />
        <Button type="submit" busy={bootstrap.isPending}>
          {t("setup.submit")}
        </Button>
      </form>
    </AuthLayout>
  );
}

export function SignInPage() {
  const { t } = useTranslation();
  const passkey = usePasskeySignIn();
  const password = usePasswordSignIn();
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    password.mutate({ email: value(form, "email"), password: String(form.get("password") ?? "") });
  };
  return (
    <AuthLayout title={t("signIn.title")}>
      <div className="flex flex-col gap-2">
        <Button
          onClick={() => passkey.mutate(undefined)}
          busy={passkey.isPending}
          icon={<KeyRound className="size-4" aria-hidden />}
        >
          {t("signIn.passkey")}
        </Button>
        <p className="text-center text-xs text-muted">{t("signIn.passkeyHint")}</p>
        <ErrorMessage error={passkey.error} />
      </div>
      <div className="flex items-center gap-3 text-xs text-muted">
        <span className="h-px flex-1 bg-line" />
        {t("signIn.or")}
        <span className="h-px flex-1 bg-line" />
      </div>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <TextField
          label={t("signIn.email")}
          name="email"
          type="email"
          required
          autoComplete="username webauthn"
        />
        <TextField
          label={t("signIn.password")}
          name="password"
          type="password"
          required
          autoComplete="current-password"
        />
        <ErrorMessage error={password.error} />
        <Button type="submit" variant="secondary" busy={password.isPending}>
          {t("signIn.submit")}
        </Button>
      </form>
    </AuthLayout>
  );
}

export function CodePage() {
  const { t } = useTranslation();
  const [kind, setKind] = useState<"totp" | "recovery">("totp");
  const verify = useCodeSignIn();
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    verify.mutate({ kind, code: value(new FormData(event.currentTarget), "code") });
  };
  return (
    <AuthLayout
      title={t("signIn.codeTitle")}
      lead={kind === "totp" ? t("signIn.codeLead") : undefined}
    >
      <form onSubmit={submit} className="flex flex-col gap-4" key={kind}>
        <TextField
          label={kind === "totp" ? t("signIn.code") : t("signIn.recoveryCode")}
          name="code"
          required
          autoFocus
          autoComplete="one-time-code"
          inputMode={kind === "totp" ? "numeric" : "text"}
          pattern={kind === "totp" ? "[0-9 ]{6,7}" : undefined}
        />
        <ErrorMessage error={verify.error} />
        <Button type="submit" busy={verify.isPending}>
          {t("signIn.verify")}
        </Button>
        <Button variant="ghost" onClick={() => setKind(kind === "totp" ? "recovery" : "totp")}>
          {kind === "totp" ? t("signIn.useRecovery") : t("signIn.useTotp")}
        </Button>
      </form>
    </AuthLayout>
  );
}
